import { AfterViewInit, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ElectionService } from 'src/app/services/election.service';
import { LcFinalResultsService } from 'src/app/services/lc-final-results.service';
import { LcUtilsService } from 'src/app/services/lc-utils.service';

@Component({
  selector: 'app-lc-hemicycle-graph',
  templateUrl: './lc-hemicycle-graph.component.html',
  styleUrls: ['./lc-hemicycle-graph.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LcHemicycleGraphComponent implements AfterViewInit, OnDestroy {

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public seatsWons: any[] = [];
  public hemicycleDots: string[] = [];
  public hemicycleTooltips: string[] = [];
  public isFinal = false;
  public candidateTitle = '';
  public LCChangeCandidateData: any[] = [];

  constructor(
    public electionService: ElectionService,
    private lcFinalResultsService: LcFinalResultsService,
    private lcUtils: LcUtilsService
  ) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.LCChange,
        DataSourcesEnum.LCStatic
      ]);
    }
    return false;
  }

  private loadChart(page: ISelectPage): void {
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    this.isFinal = election?.lcChange?.electionStatus === ElectionStatusEnum.final;

    // Clear previous data
    this.seatsWons = [];
    this.LCChangeCandidateData = [];

    if (page.pageType === PageTypeEnum.CountLCStateWide) {
      // Load seats won and candidate data from API
      election?.lcElectionCount?.parties.forEach(party => {
        this.seatsWons.push(
          {
            partyId: party.partyId,
            party: party.partyName,
            seatsThisElection: party.seatsWon,
            colourCode: party.colourCode,
          }
        );
      });

      const parties = election?.lcElectionCount?.parties;
      parties?.forEach(party => {
        const candidates = party.candidates;
        if (candidates.length > 0) {
          candidates.forEach(c => {
            this.LCChangeCandidateData.push({
              candidateName: `${c.name}`,
              party: party.partyName,
              colourCode: party.colourCode,
              orderElected: c.orderElected,
            })
          })
        }
      });

      // If election is final and we don't have complete seats data, load from CSV
      if (this.isFinal && this.shouldLoadFinalResultsFromCSV()) {
        console.log('Loading final LC results from CSV for election:', page.electionDateString);
        this.loadFinalResultsFromCSV(page.electionDateString ?? '');
      } else {
        this.fillHemicycle();
      }
    } else {
      this.fillHemicycle();
    }
  }

  /**
   * Check if we need to load final results from CSV
   * Returns true if election is final but we don't have complete seats data
   */
  private shouldLoadFinalResultsFromCSV(): boolean {
    // Check if we have any seats won data
    const hasSeatData = this.seatsWons.some(s => s.seatsThisElection > 0);
    const hasCandidateData = this.LCChangeCandidateData.length > 0;
    
    // If we don't have seats or candidate data, we should load from CSV
    return !hasSeatData || !hasCandidateData;
  }

  /**
   * Load final election results from CSV file
   */
  private loadFinalResultsFromCSV(electionDateString: string): void {
    this.lcFinalResultsService.loadOrderOfElectionReport$(electionDateString)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (finalResults) => {
          if (finalResults && finalResults.length > 0) {
            console.log('Successfully loaded final results from CSV:', finalResults.length, 'records');
            
            // Get elected candidates
            const electedCandidates = this.lcFinalResultsService.getElectedCandidates(finalResults);
            
            // Calculate seats by party
            const seatsMap = this.lcFinalResultsService.calculateSeatsWonByParty(finalResults);
            
            // Get election data for party colors
            const election = this.electionService.electionMap.get(electionDateString);
            const staticParties = election?.lcStatic?.parties || [];
            
            // Build seats won array
            this.seatsWons = [];
            seatsMap.forEach((seats, partyName) => {
              // Find matching party in static data to get color
              const staticParty = staticParties.find(p => 
                this.lcUtils.normalizePartyName(p.partyName) === partyName ||
                p.partyName.toLowerCase().includes(partyName.toLowerCase()) ||
                partyName.toLowerCase().includes(p.partyName.toLowerCase())
              );
              
              this.seatsWons.push({
                partyId: staticParty?.partyId || partyName,
                party: staticParty?.partyName || partyName,
                seatsThisElection: seats,
                colourCode: staticParty?.colourCode || this.lcUtils.getDefaultColorForParty(partyName),
              });
            });
            
            // Build candidate data array
            this.LCChangeCandidateData = [];
            electedCandidates.forEach((candidate, index) => {
              const partyName = this.lcUtils.normalizePartyName(candidate.groupFullname);
              const staticParty = staticParties.find(p => 
                this.lcUtils.normalizePartyName(p.partyName) === partyName ||
                p.partyName.toLowerCase().includes(partyName.toLowerCase()) ||
                partyName.toLowerCase().includes(p.partyName.toLowerCase())
              );
              
              this.LCChangeCandidateData.push({
                candidateName: candidate.fullname,
                party: staticParty?.partyName || partyName,
                colourCode: staticParty?.colourCode || this.lcUtils.getDefaultColorForParty(partyName),
                orderElected: index + 1,
              });
            });
            
            console.log('Seats won by party:', this.seatsWons);
            console.log('Elected candidates:', this.LCChangeCandidateData);
            
            // Fill the hemicycle visualization
            this.fillHemicycle();
          } else {
            console.warn('No final results found in CSV');
            this.fillHemicycle();
          }
        },
        (error) => {
          console.error('Error loading final results from CSV:', error);
          this.fillHemicycle();
        }
      );
  }


  private fillHemicycle(): void {
    this.hemicycleDots = [];
    this.hemicycleTooltips = [];
    let libSeats = 0;
    let labSeats = 0;
    let indSeats = 11;
    const totalSeatsContested = 11;
    this.seatsWons.sort((a, b) => b.seatsThisElection - a.seatsThisElection);
    this.seatsWons.forEach(s => {
      switch (s.party) {
        case 'Liberal Party':
          const lib = this.seatsWons.find(t => t.party.includes('Liberal'));
          if (lib) {
            libSeats = lib.seatsThisElection;
          }
          if (libSeats > 0) {
            indSeats -= libSeats;
            const candidate = this.LCChangeCandidateData.filter(c => c.party === s.party);

            for (let ncount = totalSeatsContested; ncount > totalSeatsContested - libSeats; ncount--) {
              this.hemicycleDots[ncount] = s.colourCode;
              this.hemicycleTooltips[ncount] = _.startCase(_.capitalize(candidate[totalSeatsContested - ncount].candidateName)) + ', ' + s.party;
            }
          }
          break;

        case 'Australian Labor Party':
          const lab = this.seatsWons.find(t => t.party.includes('Labor'));
          if (lab) {
            labSeats = lab.seatsThisElection;
            if (labSeats > 0) {
              indSeats -= labSeats;
              const candidate = this.LCChangeCandidateData.filter(c => c.party === s.party);

              for (let ncount = 1; ncount < labSeats + 1; ncount++) {
                this.hemicycleDots[ncount] = s.colourCode;
                this.hemicycleTooltips[ncount] = _.startCase(_.capitalize(candidate[ncount - 1].candidateName)) + ', ' + s.party;
              }
            }
          }
          break;

        default:
          const seats = s.seatsThisElection;
          let partySeats = 0;
          let seatsLabelled = labSeats + 1;
          for (let n = seatsLabelled; n < (seatsLabelled + seats); n++) {
            const candidate = this.LCChangeCandidateData.filter(c => c.party == s.party)
            this.hemicycleDots[n] = s.colourCode;
            this.hemicycleTooltips[n] = _.startCase(_.capitalize(candidate[partySeats].candidateName)) + ', ' + s.party;
            partySeats++;
            labSeats++;
          }
          break;
      }
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
  
}
